import { APIProvider, Map } from "@vis.gl/react-google-maps";
import { useEffect, useState } from "react";
import { getDataForPrivewMap } from "../api/maps/previewMapRequest";
import { PolygonsRenderer } from "../components/PolygonsRenderer";
import { type } from "@testing-library/user-event/dist/type";
import { PolylinesRenderer } from "../components/PolylinesRenderer";

const MapLegend = ({ previewData }) => {
  // Group lines by their type_line combinations
  const groupedLines = previewData.lines.reduce((acc, line) => {
    const types = line.line_details.map((detail) => detail.type_line.name);
    const key = types.sort().join("/"); // Combine types into a key (e.g., "Blue/Green")
    let color = line.line_details[0].type_line.color;
    if (types.length > 1) color = "008080"; // Black for mixed types

    if (!acc[key]) {
      acc[key] = { count: 0, color: color }; // Use the first type's color
    }
    acc[key].count += 1;
    return acc;
  }, {});

  return (
    <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm p-4 rounded-lg shadow-lg border border-gray-200 w-72 transition-opacity hover:opacity-100 opacity-90">
      <div className="text-lg font-semibold mb-1 text-gray-800">
        Légende de la carte
      </div>
      {[
        {
          color: "#800080",
          label: "Contrat",
          description: "Zone Violet",
          type: "zones",
        },
        {
          color: "#FFA500",
          label: "Zone",
          description: "Zone Orange",
          type: "sections",
        },
        {
          color: "#000000",
          label: "Rues",
          description: "Route/zones Noir",
          type: "streets",
        },
      ].map(({ color, label, description, type }) => (
        <div key={label} className="flex items-center mb-2 last:mb-0">
          <span
            className="w-4 h-4 rounded-sm mr-3 flex-shrink-0"
            style={{ backgroundColor: color }}
          />
          <span className="text-sm">
            <strong className="text-gray-800">
              {label} ({previewData[type].length}) :
            </strong>
            <span className="text-gray-600 ml-1">{description}</span>
          </span>
        </div>
      ))}
      {/* Dynamically generate legend entries for grouped lines */}
      {Object.entries(groupedLines).map(([typeName, { count, color }]) => (
        <div key={typeName} className="flex items-center mb-2 last:mb-0">
          <span
            className="w-4 h-4 rounded-sm mr-3 flex-shrink-0"
            style={{ backgroundColor: `#${color}` }}
          />
          <span className="text-sm">
            <strong className="text-gray-800">lignes ({count}) :</strong>
            <span className="text-gray-600 ml-1">
              Ligne {typeName.toLowerCase()}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
};

const PreviewMapPage = () => {
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getDataForPrivewMap();
        setPreviewData(response);
      } catch (error) {
        console.error("Error fetching preview data:", error);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="relative w-full h-screen">
      <APIProvider
        apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        region="MA"
      >
        <Map
          disableDefaultUI={false}
          options={{
            mapTypeControl: true,
            zoomControl: true,
            streetViewControl: false,
          }}
          defaultCenter={{ lat: 35.784469, lng: -5.788669 }}
          defaultZoom={14}
          gestureHandling="greedy"
          styles={[
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [{ visibility: "off" }],
            },
          ]}
        >
          {previewData?.zones && (
            <PolygonsRenderer polygons={previewData.zones} color="#800080" />
          )}
          {previewData?.sections && (
            <PolygonsRenderer polygons={previewData.sections} color="#FFA500" />
          )}
          {previewData?.streets && (
            <PolygonsRenderer polygons={previewData.streets} color="#000000" />
          )}
          {previewData?.lines && (
            <PolylinesRenderer polygons={previewData.lines} />
          )}
        </Map>
        {previewData && <MapLegend previewData={previewData} />}
      </APIProvider>
    </div>
  );
};

export default PreviewMapPage;
